import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../theme/ThemeContext';

interface PhoneCalendarHeaderProps {
  title: string;
  todayNumber: number;
  pickerOpen: boolean;
  searchOpen: boolean;
  searchQuery: string;
  onOpenMenu?: () => void;
  onTogglePicker: () => void;
  onOpenSearch: () => void;
  onCloseSearch: () => void;
  onSearchQueryChange: (value: string) => void;
  onToday: () => void;
  onOpenSheet: () => void;
}

const Icon: React.FC<{ path: string }> = ({ path }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d={path} fill="currentColor" />
  </svg>
);

const MENU_PATH = 'M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z';
const SEARCH_PATH = 'M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z';
const BACK_PATH = 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z';
const MORE_PATH = 'M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z';

export const PhoneCalendarHeader: React.FC<PhoneCalendarHeaderProps> = ({
  title,
  todayNumber,
  pickerOpen,
  searchOpen,
  searchQuery,
  onOpenMenu,
  onTogglePicker,
  onOpenSearch,
  onCloseSearch,
  onSearchQueryChange,
  onToday,
  onOpenSheet
}) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const headerStyle: React.CSSProperties = {
    background: tokens.calendar.panel,
    color: tokens.calendar.text,
    borderBottom: `1px solid ${tokens.calendar.divider}`
  };

  if (searchOpen) {
    return (
      <header className="phone-cal-header" style={headerStyle}>
        <button type="button" className="phone-cal-icon" aria-label={t('responsive.calendar.closeSearch')} onClick={onCloseSearch}>
          <Icon path={BACK_PATH} />
        </button>
        <input
          ref={searchRef}
          data-testid="calendar-search"
          type="search"
          className="phone-cal-search"
          aria-label={t('calendar.searchPlaceholder')}
          placeholder={t('calendar.searchPlaceholder')}
          value={searchQuery}
          onChange={event => onSearchQueryChange(event.target.value)}
          style={{ color: tokens.calendar.text }}
        />
      </header>
    );
  }

  return (
    <header className="phone-cal-header" style={headerStyle}>
      {onOpenMenu && (
        <button type="button" className="phone-cal-icon" aria-label={t('responsive.openMenu')} aria-controls="main-navigation" onClick={onOpenMenu}>
          <Icon path={MENU_PATH} />
        </button>
      )}
      <button
        type="button"
        className="phone-cal-title"
        data-testid="phone-calendar-title"
        aria-label={t('responsive.calendar.pickDate')}
        aria-expanded={pickerOpen}
        onClick={onTogglePicker}
        style={{ color: tokens.calendar.text }}
      >
        <span>{title}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" style={{ transform: pickerOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
          <path d="M7 10l5 5 5-5z" fill="currentColor" />
        </svg>
      </button>
      <button type="button" className="phone-cal-icon" aria-label={t('responsive.calendar.search')} onClick={onOpenSearch}>
        <Icon path={SEARCH_PATH} />
      </button>
      <button type="button" className="phone-cal-icon" data-testid="phone-calendar-today" aria-label={t('responsive.calendar.today')} onClick={onToday}>
        <span className="phone-cal-today-glyph" style={{ borderColor: tokens.calendar.text }}>{todayNumber}</span>
      </button>
      <button type="button" className="phone-cal-icon" data-testid="phone-calendar-options" aria-label={t('responsive.calendar.views')} onClick={onOpenSheet}>
        <Icon path={MORE_PATH} />
      </button>
    </header>
  );
};
